import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import {
  CustomParams,
  CreateCustomParamsPayload,
  UpdateCustomParamsPayload,
} from '@shared/models/entities/customParams.model';

/**
 * Servicio de la tabla `customparams` (parámetros por usuario).
 *
 * CRUD estándar contra `/customparams` + helpers para el patrón de uso
 * real: un registro único por usuario (buscar por userId y hacer upsert).
 */
@Injectable({ providedIn: 'root' })
export class CustomParamsService extends ApiBaseService<CustomParams> {

  constructor() {
    super('customparams');
  }

  /**
   * Registro de parámetros de un usuario, o null si aún no tiene.
   * `userId` es único en la tabla, así que a lo más hay un registro vivo.
   */
  async getByUserId(userId: string): Promise<CustomParams | null> {
    const response = await this.search({
      searchType: 'default',
      condition: { userId, deleted: false },
    });
    return Array.isArray(response.data) && response.data.length > 0
      ? (response.data[0] as CustomParams)
      : null;
  }

  /**
   * Upsert: si el usuario ya tiene registro lo actualiza (PATCH por id);
   * si no, lo crea (POST). Devuelve el registro resultante.
   */
  async saveForUser(
    userId: string,
    places: string[],
    specialties: string[],
  ): Promise<CustomParams | null> {
    const existing = await this.getByUserId(userId);

    if (existing) {
      const payload: UpdateCustomParamsPayload = {
        id: existing.id,
        places,
        specialties,
        operationType: 'single',
      };
      const response = await this.update(payload as unknown as CustomParams);
      return Array.isArray(response.data) && response.data.length > 0
        ? (response.data[0] as CustomParams)
        : (response.data as unknown as CustomParams ?? null);
    }

    const payload: CreateCustomParamsPayload = {
      userId,
      places,
      specialties,
      operationType: 'single',
    };
    const response = await this.create(payload as unknown as CustomParams);
    return Array.isArray(response.data) && response.data.length > 0
      ? (response.data[0] as CustomParams)
      : (response.data as unknown as CustomParams ?? null);
  }
}
